import React, { useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { getContactFromNumber } from 'flow/actions/meeting-actions';
import SettingRow from 'flow/components/Setting/SettingRow';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import SearchInput from 'flow/components/ui-components/SearchInput';
import useAppContext from 'flow/hooks/useAppContext';
import { Capabilities } from 'flow/utils/capability-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName, sortByName } from 'flow/utils';
import { getFullNameFromContact } from 'flow/utils/call-utils-TW';
import {
    filterByAllfileds,
    getExtensionHasCapability,
} from 'flow/utils/extension-utils-TW';

interface Props {
    title?: string;
    selectedExtensionAction?: (extensionKey: ExtensionKey) => void;
}

interface CallingMembers {
    extensionKey: ExtensionKey;
    callingEndTime: number;
}

const AddVideoParticipantModal = ({
    title,
    selectedExtensionAction,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [searchTerm, setSearchTerm] = useState([]);
    const participantsRef = useRef<HTMLDivElement | null>(null);
    const timeoutId = useRef<NodeJS.Timeout | number>(null);
    const [selectedExtension, setSelectedExtension] = useState(null);
    const meetingMembers = useAppSelector((state) =>
        state.meeting.getIn(['activeMeeting', 'members']),
    );

    const callingInProgressExtensionKeys = useAppSelector((state) =>
        state.meeting
            .getIn(['activeMeeting', 'callingMembers'])
            ?.map(({ extensionKey }: CallingMembers) => extensionKey),
    );

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    const statuses = useAppSelector((state) =>
        state.extensions.get('statuses'),
    );

    const hasVideoCapability = (e: TypedMap<Extension>) =>
        getExtensionHasCapability(e, Capabilities.VIDEO);

    /**
     * TD: member phone number uses u013244880 as sip when
     * Handle video meetings as phone calls is not enabled in Telephony settings.
     * This will cause an issue with showing u013244880's account data as participant in video conference (ex: user avatar)
     * So temporary fix will be to start video conference with handle video meetings as phone calls enabled
     * or do a backend fix.
     */
    const meetingParticipantExtensions = useMemo(
        () =>
            meetingMembers
                ?.map((member: TypedMap<never>) => {
                    const contact = dispatch<any>(
                        getContactFromNumber(member.get('number')),
                    );

                    return extensions
                        .find(
                            (extension) =>
                                extension.getIn(['contact', 'key']) ===
                                contact?.get('key'),
                        )
                        ?.get('key');
                })
                .toArray(),
        [meetingMembers, extensions],
    );

    const isAlreadyInMeeting = (extension: TypedMap<Extension>) =>
        !meetingParticipantExtensions.includes(extension.get('key'));

    const filteredExtensions = useMemo(() => {
        return extensions
            .filter(isAlreadyInMeeting)
            .filter(hasVideoCapability)
            .filter((extension: TypedMap<Extension>) =>
                filterByAllfileds(extension, searchTerm),
            )
            .sort(sortByName)
            .valueSeq();
    }, [extensions, searchTerm, meetingParticipantExtensions]);

    const onAccept = () =>
        selectedExtensionAction(selectedExtension.get('key'));

    const handleInput = (text: string) => {
        clearTimeout(timeoutId.current as number);

        timeoutId.current = setTimeout(() => {
            setSearchTerm(
                text
                    .split(/\s+/)
                    .filter((searchWord) => searchWord !== '')
                    .map((mapSearchWord) => mapSearchWord.toLowerCase()),
            );
        }, 400);

        participantsRef.current.scrollTo({ top: 0 });
    };

    return (
        <BaseModal
            open={true}
            header={title}
            disableSubmit={false}
            onSubmit={onAccept}
            onClose={() => setSelectedExtension(null)}
            confirmButtonText={i18next.t('accept')}
            disableActions={!selectedExtension}
        >
            {selectedExtension ? (
                <div>
                    <p className="text-sm/6">
                        <Trans
                            i18n={i18next}
                            i18nKey={
                                'call.canvas.videoparticipants.addselectedparticipant.confirmation'
                            }
                            values={{
                                contact: getFullNameFromContact(
                                    selectedExtension.get('contact'),
                                ),
                            }}
                            components={[<strong key={'ended-p-1'} />]}
                        />
                    </p>
                </div>
            ) : (
                <div className="flex flex-col">
                    <SearchInput
                        placeholder={i18next.t('search')}
                        iconKey="search"
                        onChange={(e) => handleInput(e.target?.value)}
                        autoFocus={true}
                    />
                    <div
                        className="scrollbar my-5 flex h-[300px] w-full flex-col gap-y-2"
                        ref={participantsRef}
                    >
                        {filteredExtensions.map(
                            (extension: TypedMap<Extension>) => {
                                const contact = extension.get('contact');
                                const extensionKey = extension.get('key');
                                const extStatus = statuses.get(extensionKey);
                                const buttonInfo =
                                    callingInProgressExtensionKeys.includes(
                                        extensionKey,
                                    )
                                        ? i18next.t('callcontrol.outgoing_call')
                                        : i18next.t('chatsettings.add');

                                return (
                                    <SettingRow
                                        key={extensionKey}
                                        cta={
                                            <Button
                                                color="primary"
                                                className="reveal-on-hover-or-focus flex !min-w-[80px]"
                                                disabled={
                                                    extStatus !== 'available' ||
                                                    callingInProgressExtensionKeys.includes(
                                                        extensionKey,
                                                    )
                                                }
                                                onClick={() =>
                                                    setSelectedExtension(
                                                        extension,
                                                    )
                                                }
                                            >
                                                {buttonInfo}
                                            </Button>
                                        }
                                    >
                                        <ContactAvatar
                                            contact={contact}
                                            lazyLoad={true}
                                            status={extStatus}
                                        />
                                        <p className="text-gray-800">
                                            {extractName(contact)}
                                        </p>
                                    </SettingRow>
                                );
                            },
                        )}
                    </div>
                </div>
            )}
        </BaseModal>
    );
};

export default AddVideoParticipantModal;
